import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Search, Plus, RefreshCw, ArrowUpRight, CheckCircle2, XCircle, ChevronDown, ChevronUp, Pencil, Save } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────

interface GapEvidence {
  users?: string[];
  attempts?: number;
  workaround?: string;
  affected_orgs?: string[];
}

interface GapSignal {
  id: string;
  gap_type: string;
  severity: string;
  title: string;
  description: string;
  affected_org_count: number;
  evidence_json: GapEvidence;
  cosmo_suggestion: string | null;
  status: string;
  notes: string | null;
  created_at: string;
}

// ── Constants ─────────────────────────────────────────────────────

const SEVERITY_META = {
  high:        { label: 'High Signal',  color: 'text-red-600',    bgColor: 'bg-red-500/10',    borderColor: 'border-red-500/30',    dot: 'bg-red-500',    badge: 'bg-red-500/10 text-red-600 border-red-500/30' },
  medium:      { label: 'Medium Signal',color: 'text-yellow-600', bgColor: 'bg-yellow-500/10', borderColor: 'border-yellow-500/30', dot: 'bg-yellow-500', badge: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30' },
  observation: { label: 'Observation',  color: 'text-blue-500',   bgColor: 'bg-blue-500/10',   borderColor: 'border-blue-500/30',   dot: 'bg-blue-400',   badge: 'bg-blue-500/10 text-blue-500 border-blue-500/30' },
};

const STATUS_LABELS: Record<string, string> = {
  open:        'Open',
  backlog:     'Backlog',
  in_progress: 'In Progress',
  dismissed:   'Dismissed',
  resolved:    'Resolved',
};

const TYPE_LABELS: Record<string, string> = {
  content: 'Content gap',
  ux:      'UX gap',
  feature: 'Feature gap',
};

// ── Main component ────────────────────────────────────────────────

export default function FeatureGapDetector() {
  const [gaps, setGaps]         = useState<GapSignal[]>([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [filter, setFilter]     = useState<'all' | 'open' | 'backlog'>('all');

  // Edit state
  const [editingId, setEditingId]   = useState<string | null>(null);
  const [editNotes, setEditNotes]   = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Add dialog
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', gap_type: 'feature', severity: 'medium',
    affected_org_count: '1', cosmo_suggestion: '',
    evidence_users: '', evidence_attempts: '', evidence_workaround: '',
  });
  const [addSaving, setAddSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('feature_gap_signals')
      .select('*')
      .not('status', 'in', '("dismissed","resolved")')
      .order('severity')
      .order('created_at', { ascending: false });
    if (error) toast.error(error.message);
    setGaps(
      (data ?? []).map(g => ({
        ...g,
        evidence_json: (g.evidence_json ?? {}) as unknown as GapEvidence,
      })) as unknown as GapSignal[]
    );
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (id: string) =>
    setExpanded(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const startEdit = (g: GapSignal) => {
    setEditingId(g.id);
    setEditNotes(g.notes ?? '');
    setEditStatus(g.status);
    setExpanded(prev => { const s = new Set(prev); s.add(g.id); return s; });
  };

  const saveEdit = async (id: string) => {
    setEditSaving(true);
    const { error } = await supabase
      .from('feature_gap_signals')
      .update({ notes: editNotes.trim() || null, status: editStatus })
      .eq('id', id);
    setEditSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Gap updated');
    setEditingId(null);
    load();
  };

  const quickStatus = async (id: string, newStatus: string) => {
    const { error } = await supabase.from('feature_gap_signals').update({ status: newStatus }).eq('id', id);
    if (error) { toast.error(error.message); return; }
    const labels: Record<string, string> = { backlog: 'Added to backlog', in_progress: 'Marked in progress', dismissed: 'Dismissed', resolved: 'Resolved' };
    toast.success(labels[newStatus] ?? 'Updated');
    load();
  };

  const addGap = async () => {
    if (!form.title.trim() || !form.description.trim()) { toast.error('Title and description are required'); return; }
    setAddSaving(true);
    const evidence: GapEvidence = {};
    if (form.evidence_users.trim())      evidence.users     = form.evidence_users.split(',').map(s => s.trim()).filter(Boolean);
    if (form.evidence_attempts.trim())   evidence.attempts  = parseInt(form.evidence_attempts);
    if (form.evidence_workaround.trim()) evidence.workaround = form.evidence_workaround.trim();

    const { error } = await supabase.from('feature_gap_signals').insert([{
      title: form.title.trim(),
      description: form.description.trim(),
      gap_type: form.gap_type,
      severity: form.severity,
      affected_org_count: parseInt(form.affected_org_count) || 0,
      cosmo_suggestion: form.cosmo_suggestion.trim() || null,
      evidence_json: (Object.keys(evidence).length ? evidence : {}) as unknown as import('@/integrations/supabase/types').Json,
      status: 'open',
    }]);
    setAddSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Gap signal added');
    setShowAdd(false);
    setForm({ title: '', description: '', gap_type: 'feature', severity: 'medium', affected_org_count: '1', cosmo_suggestion: '', evidence_users: '', evidence_attempts: '', evidence_workaround: '' });
    load();
  };

  // Filter
  const filtered = gaps.filter(g => {
    if (filter === 'open')   return g.status === 'open';
    if (filter === 'backlog') return g.status === 'backlog';
    return true;
  });

  const groups: { key: string; items: GapSignal[] }[] = [
    { key: 'high',        items: filtered.filter(g => g.severity === 'high') },
    { key: 'medium',      items: filtered.filter(g => g.severity === 'medium') },
    { key: 'observation', items: filtered.filter(g => g.severity === 'observation') },
  ].filter(g => g.items.length > 0);

  const GapCard = ({ gap }: { gap: GapSignal }) => {
    const meta   = SEVERITY_META[gap.severity as keyof typeof SEVERITY_META] ?? SEVERITY_META.medium;
    const isOpen = expanded.has(gap.id);
    const isEditing = editingId === gap.id;

    return (
      <Card className="overflow-hidden">
        <div
          className="flex items-start gap-3 p-4 cursor-pointer hover:bg-muted/20"
          onClick={() => !isEditing && toggleExpand(gap.id)}
        >
          <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${meta.dot}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Badge variant="outline" className={`text-xs ${meta.badge}`}>{meta.label}</Badge>
              <span className="text-xs text-muted-foreground">{TYPE_LABELS[gap.gap_type] ?? gap.gap_type}</span>
              {gap.affected_org_count > 0 && (
                <span className="text-xs text-muted-foreground">{gap.affected_org_count} org{gap.affected_org_count !== 1 ? 's' : ''}</span>
              )}
              <Badge variant="outline" className="text-xs ml-auto">{STATUS_LABELS[gap.status] ?? gap.status}</Badge>
            </div>
            <p className="font-medium text-sm">{gap.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{gap.description}</p>
          </div>
          {!isEditing && (
            <div className="shrink-0">
              {isOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </div>
          )}
        </div>

        {(isOpen || isEditing) && (
          <div className="border-t border-border/50 px-4 pb-4 pt-3 space-y-3">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Description</p>
              <p className="text-sm text-muted-foreground">{gap.description}</p>
            </div>

            {/* Evidence */}
            {(gap.evidence_json.users?.length || gap.evidence_json.attempts || gap.evidence_json.workaround) && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Evidence</p>
                <div className="space-y-0.5">
                  {gap.evidence_json.users?.length && (
                    <p className="text-sm text-muted-foreground">Users: {gap.evidence_json.users.join(', ')}</p>
                  )}
                  {gap.evidence_json.attempts && (
                    <p className="text-sm text-muted-foreground">{gap.evidence_json.attempts} attempts</p>
                  )}
                  {gap.evidence_json.workaround && (
                    <p className="text-sm text-muted-foreground">Workaround: {gap.evidence_json.workaround}</p>
                  )}
                </div>
              </div>
            )}

            {gap.cosmo_suggestion && (
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Suggested action</p>
                <p className="text-sm">{gap.cosmo_suggestion}</p>
              </div>
            )}

            {isEditing ? (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs">Status</Label>
                  <Select value={editStatus} onValueChange={setEditStatus}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="backlog">Backlog</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="dismissed">Dismissed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Notes</Label>
                  <Textarea rows={2} value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="Context, decisions, next steps…" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => saveEdit(gap.id)} disabled={editSaving}>
                    <Save className="h-3.5 w-3.5 mr-1.5" />{editSaving ? 'Saving…' : 'Save'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                </div>
              </>
            ) : (
              <>
                {gap.notes && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Notes</p>
                    <p className="text-sm text-muted-foreground">{gap.notes}</p>
                  </div>
                )}
                <Separator />
                <div className="flex flex-wrap gap-2">
                  {gap.status === 'open' && (
                    <Button size="sm" variant="outline" onClick={() => quickStatus(gap.id, 'backlog')}>
                      Add to backlog
                    </Button>
                  )}
                  {(gap.status === 'open' || gap.status === 'backlog') && (
                    <Button size="sm" variant="outline" onClick={() => quickStatus(gap.id, 'in_progress')}>
                      Mark in progress
                    </Button>
                  )}
                  {gap.status === 'in_progress' && (
                    <Button size="sm" variant="outline" className="text-green-600" onClick={() => quickStatus(gap.id, 'resolved')}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Resolve
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => startEdit(gap)}>
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />Edit
                  </Button>
                  <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => quickStatus(gap.id, 'dismissed')}>
                    <XCircle className="h-3.5 w-3.5 mr-1.5" />Dismiss
                  </Button>
                </div>
              </>
            )}
            <p className="text-xs text-muted-foreground">Detected {formatDistanceToNow(new Date(gap.created_at), { addSuffix: true })}</p>
          </div>
        )}
      </Card>
    );
  };

  // Stats
  const totalOpen = gaps.filter(g => g.status === 'open').length;
  const highSignals = gaps.filter(g => g.severity === 'high').length;

  return (
    <div className="p-6 space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Feature Gap Detector</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Patterns in user behavior that point to missing features, content, or UX issues</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />Add Gap Signal
          </Button>
        </div>
      </div>

      {/* Quick stats */}
      {gaps.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-muted/30 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold">{gaps.length}</p>
            <p className="text-xs text-muted-foreground">Active signals</p>
          </div>
          <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-red-600">{highSignals}</p>
            <p className="text-xs text-muted-foreground">High priority</p>
          </div>
          <div className="bg-muted/30 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold">{totalOpen}</p>
            <p className="text-xs text-muted-foreground">Unactioned</p>
          </div>
        </div>
      )}

      {/* Filter pills */}
      <div className="flex gap-2">
        {(['all', 'open', 'backlog'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
          >
            {f === 'all' ? 'All' : f === 'open' ? 'Open' : 'Backlog'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Search className="h-5 w-5 animate-pulse mr-2" /><span className="text-sm">Loading signals…</span>
        </div>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Search className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <p className="font-medium mb-1">No gap signals</p>
            <p className="text-sm text-muted-foreground mb-4">
              Add signals manually as you observe users struggling, hitting dead ends, or creating workarounds. Patterns across 3+ clients = high signal.
            </p>
            <Button onClick={() => setShowAdd(true)}><Plus className="h-4 w-4 mr-1.5" />Add Gap Signal</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {groups.map(group => {
            const meta = SEVERITY_META[group.key];
            return (
              <div key={group.key}>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-2 h-2 rounded-full ${meta.dot}`} />
                  <h2 className={`text-sm font-semibold uppercase tracking-wide ${meta.color}`}>{meta.label}</h2>
                  <Badge variant="outline" className={`text-xs ${meta.badge}`}>{group.items.length}</Badge>
                </div>
                <div className="space-y-2">{group.items.map(g => <GapCard key={g.id} gap={g} />)}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add gap dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Gap Signal</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Gap type</Label>
                <Select value={form.gap_type} onValueChange={v => setForm(p => ({ ...p, gap_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="content">Content gap</SelectItem>
                    <SelectItem value="ux">UX gap</SelectItem>
                    <SelectItem value="feature">Feature gap</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Severity</Label>
                <Select value={form.severity} onValueChange={v => setForm(p => ({ ...p, severity: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High signal</SelectItem>
                    <SelectItem value="medium">Medium signal</SelectItem>
                    <SelectItem value="observation">Observation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Industry-specific prompts missing" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description *</Label>
              <Textarea rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="What are users trying to do? What happened when Cosmo couldn't help?" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Affected orgs</Label>
                <Input type="number" min={0} value={form.affected_org_count} onChange={e => setForm(p => ({ ...p, affected_org_count: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Attempts / occurrences</Label>
                <Input type="number" min={0} value={form.evidence_attempts} onChange={e => setForm(p => ({ ...p, evidence_attempts: e.target.value }))} placeholder="—" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Users who hit this (comma-separated)</Label>
              <Input value={form.evidence_users} onChange={e => setForm(p => ({ ...p, evidence_users: e.target.value }))} placeholder="e.g. Sarah Chen, Marcus Webb" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Current workaround</Label>
              <Input value={form.evidence_workaround} onChange={e => setForm(p => ({ ...p, evidence_workaround: e.target.value }))} placeholder="e.g. Copy/paste via Slack" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Suggested fix / next step</Label>
              <Textarea rows={2} value={form.cosmo_suggestion} onChange={e => setForm(p => ({ ...p, cosmo_suggestion: e.target.value }))} placeholder="e.g. Build Finance + Legal prompt packs as next content priority" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={addGap} disabled={addSaving}>{addSaving ? 'Adding…' : 'Add Signal'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
